import { Bar, BarChart, Cell, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { GapBucket } from '../api/types';
import { count } from '../utils/format';

import { ChartTooltip } from './ChartTooltip';
import { CHROME, DIVERGE } from './palette';

/**
 * Ordered buckets of "how far our price sits from the cheapest offer". The
 * measure has a natural zero with opposite meanings either side, so the fill is
 * a diverging scale (blue = we undercut, red = we are undercut) with a neutral
 * midpoint — never a rainbow, and never one hue per bar.
 */
const BUCKET_ORDER: string[] = [
  'below -20%',
  '-20% to -10%',
  '-10% to -5%',
  '-5% to 0%',
  'at market low',
  '0% to 5%',
  '5% to 10%',
  '10% to 20%',
  'above 20%',
];

const BUCKET_FILL: Record<string, string> = {
  'below -20%': DIVERGE.low,
  '-20% to -10%': DIVERGE.low,
  '-10% to -5%': '#7fb2ee',
  '-5% to 0%': '#a8c9f2',
  'at market low': CHROME.inkMuted,
  '0% to 5%': '#f0a5a5',
  '5% to 10%': DIVERGE.high,
  '10% to 20%': DIVERGE.highSoft,
  'above 20%': '#a82c2c',
};

interface Row {
  label: string;
  count: number;
}

export function GapHistogram({ buckets }: { buckets: GapBucket[] }) {
  const byLabel = new Map(buckets.map((bucket) => [bucket.label, Number(bucket.count)]));
  const rows: Row[] = BUCKET_ORDER.map((label) => ({ label, count: byLabel.get(label) ?? 0 }));
  const total = rows.reduce((sum, row) => sum + row.count, 0);

  if (total === 0) {
    return <div className="state">No scanned products to compare yet.</div>;
  }

  return (
    <>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 0 }} barCategoryGap={4}>
          <CartesianGrid stroke={CHROME.grid} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: CHROME.inkMuted, fontSize: 10 }}
            axisLine={{ stroke: CHROME.axis }}
            tickLine={false}
            interval={0}
            angle={-32}
            textAnchor="end"
            height={62}
          />
          <YAxis
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={38}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const row = payload[0].payload as Row;

              return (
                <ChartTooltip
                  title={row.label}
                  rows={[
                    {
                      key: 'count',
                      label: 'Products',
                      value: count(row.count),
                      color: BUCKET_FILL[row.label],
                    },
                    {
                      key: 'share',
                      label: 'Share',
                      value: `${((row.count / total) * 100).toFixed(1)}%`,
                    },
                  ]}
                />
              );
            }}
          />
          <Bar isAnimationActive={false} dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={54}>
            {rows.map((row) => (
              <Cell key={row.label} fill={BUCKET_FILL[row.label]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="legend">
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: DIVERGE.low }} />
          We undercut the market
        </span>
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: CHROME.inkMuted }} />
          Level with the market low
        </span>
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: DIVERGE.high }} />
          Somebody undercuts us
        </span>
      </div>
    </>
  );
}
